import * as Sentry from '@sentry/nextjs';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { AuthPhase } from '@auth/authPhase';
import { EventEmitter, EventListener, EventType, eventEmitter } from '@event-emitter';
import { APIRequestLabel, Page } from './consts';
import * as handlers from './handlers';
import { callHttpPost } from './http';
import * as localStoregeHandlers from './localstorege_handler';

import { ImprvApiService } from './hooks/useImprvApi';
import {
  AccountProfileModel,
  ActivationParentType,
  ActivationPhraseTemplateModel,
  ActivationPhraseTemplateType,
  ActivationRuleModel,
  AssignmentTaskStateModel,
  AvailabilityModel,
  BlockAssertionSpecModel,
  BlockCreateModel,
  BlockExerciseModel,
  BlockModel,
  BlockType,
  BlockUpdateModel,
  ChatbotDataEntryModel,
  ChatbotSettingsModel,
  ChatbotTestRequestModel,
  CreateNodesBatchModel,
  CreateOrUpdateStrategyNodeModel,
  CreateOrUpdateStrategyPathModel,
  CreatedGoalSubscriptionModel,
  GarminConnectionModel,
  GetAvailabilitiesParamsModel,
  GetGoalSubscriptionsParamsModel,
  GoalModel,
  GoalPatchModel,
  GoalSubscriptionModel,
  MasterHelloModel,
  StrategyModel,
  SuccessCreatedEntity,
  SupplyModel,
  TagModel,
  TaskCreateModel,
  TaskModel,
  TaskUpdateModel,
  ToastModel,
  VersionedEntityType
} from './models';
import { GoalUsersFilter } from './models/shared';

export interface GoalSubscriptionStrategyFinishedModalState {
  isOpen: boolean;
  goalSubscriptionId: number | null;
}

export interface BuyImprvCoinsModalState {
  isOpen: boolean;
}

export interface ActionResult {
  success: boolean;
  error?: any;
}

export interface APICallStatus {
  response: any;
  error: any;
  isLoading: boolean;
}

export const createAssignmentTaskKey = (nodeEntityId: string, goalSubscriptionId: number) =>
  `${nodeEntityId}--${goalSubscriptionId}`;

export interface AppState {
  // GENERAL
  apiCallStatus: {
    [key in APIRequestLabel]: APICallStatus;
  };

  // GLOBAL MODIFIER ALLOWING US SLOW TRANSITION TOWARDS THE IN MEMORY STORAGE
  setter: (data: any) => void;
  getter: (key: string) => any;

  // EVENTS
  eventEmitter: EventEmitter;
  onEvent: <T>(event: EventType, listener: EventListener<T>) => () => void;
  emitEvent: <T>(event: EventType, data: T) => void;

  setPageLoading: (page: string, isLoading: boolean) => void;
  pageLoading: {
    [Page.EXPLORE]: boolean;
    [Page.GOAL_SUBSCRIPTIONS]: boolean;
    [Page.CONVERSATION]: boolean;
  };
  // AUTH
  authPhase: AuthPhase;
  setAuthPhase: (phase: AuthPhase) => void;

  // LOCAL STORAGE STATE
  introIsDone: boolean;
  setIntroIsDone: (isDone: boolean) => void;
  getIntroIsDone: () => boolean;
  initIntroIsDone: () => boolean;

  videoPlaybackSpeed: number;
  setVideoPlaybackSpeed: (speed: number) => void;
  getVideoPlaybackSpeed: () => number;
  initVideoPlaybackSpeed: () => number;

  // BACKWARD COMPATIBILITY: we're planning to migrate all the calls to the new useImprvApi hook
  // but in the meantime we must pass the token directly to the handler, via the store
  imprvApi: ImprvApiService;
  setImprvApi: (imprvApi: ImprvApiService) => void;
  getImprvApi: () => ImprvApiService;

  // TOASTS
  toast: ToastModel;
  setToast: (toast: ToastModel) => void;

  // ACCOUNT
  accountProfile: AccountProfileModel;
  getAccountProfile: (id: string, translator: any) => Promise<void>;

  // MASTER
  masterHello: MasterHelloModel;
  masterSayHello: () => Promise<void>;

  // GOALS
  createGoal: (goal: GoalPatchModel) => Promise<string | void>;
  updateGoal: (goalEntityId: string, goal: GoalPatchModel) => Promise<boolean>;
  publishGoal: (goalEntityId: string) => Promise<boolean>;
  deleteGoal: (goalEntityId: string) => Promise<void>;

  goals: GoalModel[];
  getGoalsOld: (params?: {
    query?: string;
    semanticQuery?: string;
    filters?: Record<any, boolean>;
  }) => Promise<void>;

  goalsLibrary: GoalModel[];
  getGoalsLibrary: (params?: {
    query?: string;
    semanticQuery?: string;
    filters?: Record<any, boolean>;
  }) => Promise<void>;

  goal: GoalModel;
  getGoalOld: (id: string, maxVersionType?: VersionedEntityType) => Promise<GoalModel>;
  // CHATBOTS
  getChatbotDataEntries: (params: {
    chatbotEntityId: string;
    maxVersionType?: VersionedEntityType;
    semanticQuery?: string;
  }) => Promise<void>;
  createChatbotDataEntry: (
    chatbotEntityId: string,
    payload: ChatbotDataEntryModel
  ) => Promise<void>;
  updateChatbotDataEntry: (
    chatbotEntityId: string,
    entryEntityId: string,
    payload: ChatbotDataEntryModel
  ) => Promise<void>;
  deleteChatbotDataEntry: (chatbotEntityId: string, entryEntityId: string) => Promise<void>;
  chatbotDataEntries: ChatbotDataEntryModel[];
  setChatbotTestRequest: (chatbotEntityId: string, payload: ChatbotTestRequestModel) => void;
  publishChatbot: (chatbotEntityId: string) => Promise<void>;
  getChatbotSettings: (chatbotEntityId: string) => Promise<void>;
  setChatbotSettings: (payload: ChatbotSettingsModel) => void;
  updateChatbotSettings: (chatbotEntityId: string, payload: ChatbotSettingsModel) => Promise<void>;
  chatbotSettings: ChatbotSettingsModel;

  chatbotTestRequests: Map<string, ChatbotTestRequestModel>;
  createChatbotTestRequest: (
    chatbotEntityId: string,
    payload: ChatbotTestRequestModel,
    params: { chatbotMaxVersionType?: VersionedEntityType }
  ) => Promise<void>;
  getChatbotTestRequest: (chatbotEntityId: string, requestId: number) => Promise<void>;
  updateChatbotTestRequest: (
    chatbotEntityId: string,
    requestId: number,
    payload: ChatbotTestRequestModel
  ) => Promise<void>;

  // GOAL SUBSCRIPTIONS
  goalSubscriptions: GoalSubscriptionModel[];
  getGoalSubscriptionsOld: (
    params: GetGoalSubscriptionsParamsModel
  ) => Promise<GoalSubscriptionModel[]>;

  goalSubscription: GoalSubscriptionModel;
  getGoalSubscription: (goalSubscriptionId: number) => Promise<GoalSubscriptionModel>;

  createGoalSubscription: (
    goalEntitId: string,
    payload: { goalEntityId: string; priority?: string; deadline?: Date }
  ) => Promise<CreatedGoalSubscriptionModel>;
  updateGoalSubscription: (
    goalEntityId: string,
    goalSubscriptionId: number,
    payload: { priority?: string; deadline?: Date; status?: string }
  ) => Promise<void>;
  goalSubscriptionChangedTo: 'ACTIVE' | 'COMPLETED' | null;
  setGoalSubscriptionChangedTo: (status: 'ACTIVE' | 'COMPLETED') => void;
  syncGoalSubscription: (
    goalEntityId: string,
    goalSubscriptionId: number,
    status: string,
    previousStatus: string
  ) => Promise<void>;

  // TASKS
  task: TaskModel;
  taskDone: boolean;
  taskOutput: { [blockEntityId: string]: any };
  taskErrors: { [blockEntityId: string]: string };
  taskSuccesses: { [blockEntityId: string]: string };
  taskWarnings: { [blockEntityId: string]: string };
  getTask: (
    taskEntityId: string,
    params?: { maxVersionType?: VersionedEntityType }
  ) => Promise<TaskModel | undefined>;
  createTask: (task: TaskCreateModel) => Promise<string | undefined>;
  cloneTask: (taskEntityId: string, titlePrefix: string) => Promise<string | undefined>;
  updateTask: (taskEntityId: string, task: TaskUpdateModel) => Promise<boolean | undefined>;
  deleteTask: (taskEntityId: string) => Promise<void>;
  tasks: TaskModel[];
  getTasks: (params?: {
    goalId?: number;
    maxVersionType?: VersionedEntityType;
    query?: string;
    withBlocks?: boolean;
  }) => Promise<void>;

  createBlock: (taskEntityId: string, block: BlockCreateModel) => Promise<string | undefined>;
  getBlock: (taskEntityId: string, blockEntityId: string) => Promise<BlockModel | undefined>;
  updateBlock: (
    taskEntityId: string,
    blockEntityId: string,
    block: BlockUpdateModel
  ) => Promise<boolean | null | undefined>;
  deleteBlock: (taskEntityId: string, blockEntityId: string) => Promise<boolean>;
  cloneBlock: (taskEntityId: string, block: BlockCreateModel) => Promise<string | undefined>;

  getBlockExercises: (blockType: BlockType) => Promise<void>;
  blockExercises: { [blockType: string]: BlockExerciseModel[] };

  getBlockAssertionSpecs: (blockType: BlockType) => Promise<void>;
  blockAssertionSpecs: { [blockType: string]: BlockAssertionSpecModel[] };

  //FIXME move this to the AssignmentTaskContext
  resetBlockOutput: () => void;
  setBlockOutput: (blockEntityId: string, value: any) => void;

  // doTaskAssignment: (taskEntityId: string, taskAssignmentId: number) => Promise<void>;
  // declineTaskAssignment: (taskEntityId: string, taskAssignmentId: number) => Promise<void>;

  // LIKES
  like: (type: string, entityId: number | string) => Promise<boolean>;
  unlike: (type: string, entityId: number | string) => Promise<boolean>;

  // GARMIN
  garminConnections: GarminConnectionModel[];
  getGarminConnections: () => Promise<void>;
  createGarminConnection: () => Promise<void>;
  authorizeGarminConnection: (oauthVerifier: string) => Promise<void>;

  // SCHEDULE
  lastScheduleQueueDate: string | null;
  setLastScheduleQueueDate: (date: string) => void;
  getLastScheduleQueueDate: () => string | null;
  initLastScheduleQueueDate: () => string | null;
  availabilities: AvailabilityModel[];
  getAvailabilities: (params: GetAvailabilitiesParamsModel) => Promise<AvailabilityModel[]>;
  createOrUpdateAvailability: (availability: any) => Promise<void>;
  schedulingInProgress: boolean;
  schedulingInProgressTimestamp: number | null;
  setSchedulingInProgress: (inProgress: boolean, schedulingInProgressTimestamp: number) => void;

  // ASSIGNMENT TASK STATE
  assignmentTaskStates: Map<string, AssignmentTaskStateModel>;
  getAssignmentTaskState: (
    nodeEntityId: string,
    goalSubscriptionId: number
  ) => AssignmentTaskStateModel | null;
  setAssignmentTaskState: (
    nodeEntityId: string,
    goalSubscriptionId: number,
    state: AssignmentTaskStateModel
  ) => void;

  // SUPPLIES
  getSupplies: (blockType: BlockType) => Promise<void>;
  supplies: { [blockType: string]: SupplyModel[] };

  // ACTIVATIONS
  getActivationPhraseTemplates: (params?: {
    parentType: ActivationParentType;
    type?: ActivationPhraseTemplateType;
  }) => Promise<ActivationPhraseTemplateModel[]>;
  createActivationRule: (rule: any) => Promise<{ entityId: string }>;
  updateActivationRule: (entityId: string, rule: any) => Promise<void>;
  getActivationRule: (entityId: string) => Promise<ActivationRuleModel>;
  deleteActivationRule: (entityId: string) => Promise<void>;

  // TAGS
  getTags: () => Promise<void>;
  getTag: (tagEntityId: string) => Promise<TagModel>;
  createTag: (tag: TagModel) => Promise<SuccessCreatedEntity>;
  updateTag: (tagEntityId: string, tag: TagModel) => Promise<void>;
  deleteTag: (tagEntityId: string) => Promise<void>;
  tags: TagModel[];
  // STRATEGIES
  getStrategy: (
    strategyEntityId: string,
    params: { maxVersionType?: VersionedEntityType }
  ) => Promise<StrategyModel>;
  publishStrategy: (strategyEntityId: string) => Promise<boolean>;
  publishTask: (taskEntityId: string) => Promise<boolean>;
  createStrategyPath: (
    strategyEntityId: string,
    path: CreateOrUpdateStrategyPathModel
  ) => Promise<{ entityId: string }>;
  updateStrategyPath: (
    strategyEntityId: string,
    pathEntityId: string,
    path: CreateOrUpdateStrategyPathModel
  ) => Promise<void>;
  deleteStrategyPath: (strategyEntityId: string, pathEntityId: string) => Promise<void>;
  createStrategyNodesBatch: (
    strategyEntityId: string,
    pathEntityId: string,
    nodesBatch: CreateNodesBatchModel
  ) => Promise<{ entityId: string }>;
  createStrategyNode: (
    strategyEntityId: string,
    pathEntityId: string,
    node: CreateOrUpdateStrategyNodeModel
  ) => Promise<{ entityId: string }>;
  updateStrategyNode: (
    strategyEntityId: string,
    pathEntityId: string,
    nodeEntityId: string,
    node: CreateOrUpdateStrategyNodeModel
  ) => Promise<void>;
  deleteStrategyNode: (
    strategyEntityId: string,
    pathEntityId: string,
    nodeEntityId: string
  ) => Promise<void>;
  cancelPendingSubscription: (goalSubscriptionId: number) => Promise<void>;

  // GLOBAL MODALS
  goalSubscriptionStrategyFinishedModalState: GoalSubscriptionStrategyFinishedModalState;
  setGoalSubscriptionStrategyFinishedModalState: (
    state: GoalSubscriptionStrategyFinishedModalState
  ) => void;

  buyImprvCoinsModalState: BuyImprvCoinsModalState;
  openBuyImprvCoinsModal: () => void;
  closeBuyImprvCoinsModal: () => void;

  // UI -> GAME STATE (for components outside AssignmentTaskContext)
  gameActive: boolean;
  setGameActive: (active: boolean) => void;

  // UI -> MENTOR APPROVE (bridge between FeedbackEditor and navigation)
  mentorApproveHandler: (() => void) | null;
  setMentorApproveHandler: (handler: (() => void) | null) => void;
  isMentorApproving: boolean;
  setMentorApproving: (isApproving: boolean) => void;

  // UI -> GOAL USERS QUERY (persists filter/search across navigation, scoped per goalEntityId)
  goalUsersQueryState: Record<string, { activeFilter: GoalUsersFilter; searchQuery: string }>;
  setGoalUsersActiveFilter: (goalEntityId: string, filter: GoalUsersFilter) => void;
  setGoalUsersSearchQuery: (goalEntityId: string, query: string) => void;
}

const getDefaultApiCallStatus = (): APICallStatus => {
  return {
    response: null,
    error: null,
    isLoading: false
  };
};

export const useStore = createWithEqualityFn<AppState>()(
  (set, get) => ({
    // GENERAL
    apiCallStatus: {
      // MASTER
      [APIRequestLabel.MASTER_SAY_HELLO]: getDefaultApiCallStatus(),

      // ACCOUNT
      [APIRequestLabel.GET_ACCOUNT_PROFILE]: getDefaultApiCallStatus(),

      // GOALS
      [APIRequestLabel.CREATE_GOAL]: getDefaultApiCallStatus(),
      [APIRequestLabel.GET_GOALS]: getDefaultApiCallStatus(),
      [APIRequestLabel.GET_GOALS_LIBRARY]: getDefaultApiCallStatus(),
      [APIRequestLabel.GET_GOAL]: getDefaultApiCallStatus(),
      [APIRequestLabel.UPDATE_GOAL]: getDefaultApiCallStatus(),
      [APIRequestLabel.PUBLISH_GOAL]: getDefaultApiCallStatus(),
      [APIRequestLabel.DELETE_GOAL]: getDefaultApiCallStatus(),
      [APIRequestLabel.LIKE]: getDefaultApiCallStatus(),
      [APIRequestLabel.UNLIKE]: getDefaultApiCallStatus(),

      // GOAL SUBSCRIPTIONS
      [APIRequestLabel.GET_GOAL_SUBSCRIPTION]: getDefaultApiCallStatus(),
      [APIRequestLabel.GET_GOAL_SUBSCRIPTIONS]: getDefaultApiCallStatus(),
      [APIRequestLabel.CREATE_GOAL_SUBSCRIPTION]: getDefaultApiCallStatus(),
      [APIRequestLabel.UPDATE_GOAL_SUBSCRIPTION]: getDefaultApiCallStatus(),

      // CHATBOT
      [APIRequestLabel.GET_CHATBOT_DATA_ENTRIES]: getDefaultApiCallStatus(),
      [APIRequestLabel.GET_CHATBOT_DATA_ENTRY]: getDefaultApiCallStatus(),
      [APIRequestLabel.UPDATE_CHATBOT_DATA_ENTRY]: getDefaultApiCallStatus(),
      [APIRequestLabel.DELETE_CHATBOT_DATA_ENTRY]: getDefaultApiCallStatus(),
      [APIRequestLabel.CREATE_CHATBOT_DATA_ENTRY]: getDefaultApiCallStatus(),
      [APIRequestLabel.GET_CHATBOT_SETTINGS]: getDefaultApiCallStatus(),
      [APIRequestLabel.UPDATE_CHATBOT_SETTINGS]: getDefaultApiCallStatus(),
      [APIRequestLabel.CREATE_CHATBOT_TEST_REQUEST]: getDefaultApiCallStatus(),
      [APIRequestLabel.GET_CHATBOT_TEST_REQUEST]: getDefaultApiCallStatus(),
      [APIRequestLabel.UPDATE_CHATBOT_TEST_REQUEST]: getDefaultApiCallStatus(),
      [APIRequestLabel.PUBLISH_CHATBOT]: getDefaultApiCallStatus(),

      // TASKS
      [APIRequestLabel.GET_TASK]: getDefaultApiCallStatus(),
      [APIRequestLabel.GET_TASKS]: getDefaultApiCallStatus(),
      [APIRequestLabel.CREATE_TASK]: getDefaultApiCallStatus(),
      [APIRequestLabel.CLONE_TASK]: getDefaultApiCallStatus(),
      [APIRequestLabel.UPDATE_TASK]: getDefaultApiCallStatus(),
      [APIRequestLabel.DELETE_TASK]: getDefaultApiCallStatus(),
      [APIRequestLabel.CREATE_TASK_BLOCK]: getDefaultApiCallStatus(),
      [APIRequestLabel.CLONE_TASK_BLOCK]: getDefaultApiCallStatus(),
      [APIRequestLabel.UPDATE_TASK_BLOCK]: getDefaultApiCallStatus(),
      [APIRequestLabel.DELETE_TASK_BLOCK]: getDefaultApiCallStatus(),
      [APIRequestLabel.GET_TASK_BLOCK]: getDefaultApiCallStatus(),
      [APIRequestLabel.GET_TASK_BLOCK_EXERCISES]: getDefaultApiCallStatus(),
      [APIRequestLabel.GET_TASK_BLOCK_ASSERTION_SPECS]: getDefaultApiCallStatus(),

      // CONVERSATIONS
      [APIRequestLabel.GET_CONVERSATION]: getDefaultApiCallStatus(),
      [APIRequestLabel.GET_CONVERSATIONS]: getDefaultApiCallStatus(),
      [APIRequestLabel.GET_CONVERSATION_MESSAGE]: getDefaultApiCallStatus(),
      [APIRequestLabel.GET_CONVERSATION_MESSAGES]: getDefaultApiCallStatus(),
      [APIRequestLabel.GET_CONVERSATION_MESSAGES_BY_CREATED_AT]: getDefaultApiCallStatus(),
      [APIRequestLabel.CREATE_CONVERSATION_MESSAGE]: getDefaultApiCallStatus(),
      [APIRequestLabel.GET_CONVERSATION_ATTACHMENTS]: getDefaultApiCallStatus(),
      [APIRequestLabel.MARK_CONVERSATION_MESSAGE_AS_READ]: getDefaultApiCallStatus(),
      [APIRequestLabel.MARK_CONVERSATION_MESSAGE_AS_IRRELEVANT]: getDefaultApiCallStatus(),
      [APIRequestLabel.MARK_CONVERSATION_MESSAGE_AS_REPLIED]: getDefaultApiCallStatus(),
      [APIRequestLabel.MARK_MESSAGE_RECOMMENDATION_AS_DECLINED]: getDefaultApiCallStatus(),
      [APIRequestLabel.CREATE_CONVERSATION_PARTICIPANT_PRESENCE]: getDefaultApiCallStatus(),

      // GARMIN
      [APIRequestLabel.GET_GARMIN_CONNECTIONS]: getDefaultApiCallStatus(),
      [APIRequestLabel.CREATE_GARMIN_CONNECTION]: getDefaultApiCallStatus(),
      [APIRequestLabel.AUTHORIZE_GARMIN_CONNECTION]: getDefaultApiCallStatus(),
      [APIRequestLabel.MARK_CONVERSATION_MESSAGE_DECLINED_FOR_CHATBOT]: getDefaultApiCallStatus(),

      // SCHEDULE
      [APIRequestLabel.GET_AVAILABILITIES]: getDefaultApiCallStatus(),
      [APIRequestLabel.CREATE_OR_UPDATE_AVAILABILITY]: getDefaultApiCallStatus(),
      // ASSIGNMENTS
      [APIRequestLabel.GET_ASSIGNMENTS]: getDefaultApiCallStatus(),
      [APIRequestLabel.GET_ASSIGNMENT]: getDefaultApiCallStatus(),
      [APIRequestLabel.UPDATE_ASSIGNMENT]: getDefaultApiCallStatus(),
      [APIRequestLabel.CREATE_BLOCK_OUTPUT_LOGS]: getDefaultApiCallStatus(),
      [APIRequestLabel.GET_BLOCK_OUTPUT_RESULTS]: getDefaultApiCallStatus(),

      // SUPPLIES
      [APIRequestLabel.GET_SUPPLIES]: getDefaultApiCallStatus(),

      // ACTIVATIONS
      [APIRequestLabel.GET_ACTIVATION_PHRASE_TEMPLATES]: getDefaultApiCallStatus(),
      [APIRequestLabel.CREATE_ACTIVATION_RULE]: getDefaultApiCallStatus(),
      [APIRequestLabel.GET_ACTIVATION_RULES]: getDefaultApiCallStatus(),
      [APIRequestLabel.GET_ACTIVATION_RULE]: getDefaultApiCallStatus(),
      [APIRequestLabel.UPDATE_ACTIVATION_RULE]: getDefaultApiCallStatus(),
      [APIRequestLabel.DELETE_ACTIVATION_RULE]: getDefaultApiCallStatus(),

      // TAGS
      [APIRequestLabel.GET_TAGS]: getDefaultApiCallStatus(),
      [APIRequestLabel.CREATE_TAG]: getDefaultApiCallStatus(),
      [APIRequestLabel.GET_TAG]: getDefaultApiCallStatus(),
      [APIRequestLabel.UPDATE_TAG]: getDefaultApiCallStatus(),
      [APIRequestLabel.DELETE_TAG]: getDefaultApiCallStatus(),

      // STRATEGIES
      [APIRequestLabel.GET_STRATEGY]: getDefaultApiCallStatus(),
      [APIRequestLabel.PUBLISH_STRATEGY]: getDefaultApiCallStatus(),
      [APIRequestLabel.CREATE_STRATEGY_PATH]: getDefaultApiCallStatus(),
      [APIRequestLabel.UPDATE_STRATEGY_PATH]: getDefaultApiCallStatus(),
      [APIRequestLabel.DELETE_STRATEGY_PATH]: getDefaultApiCallStatus(),
      [APIRequestLabel.CREATE_STRATEGY_NODES_BATCH]: getDefaultApiCallStatus(),
      [APIRequestLabel.CREATE_STRATEGY_NODE]: getDefaultApiCallStatus(),
      [APIRequestLabel.UPDATE_STRATEGY_NODE]: getDefaultApiCallStatus(),
      [APIRequestLabel.DELETE_STRATEGY_NODE]: getDefaultApiCallStatus(),
      [APIRequestLabel.CANCEL_PENDING_SUBSCRIPTION]: getDefaultApiCallStatus()
    },

    // GLOBAL MODIFIER ALLOWING US SLOW TRANSITION TOWARDS THE IN MEMORY STORAGE
    setter: (data: any) => {
      set({
        ...get(),
        ...data
      });
    },
    getter: (key: string) => {
      return get()[key];
    },

    pageLoading: {
      [Page.EXPLORE]: null,
      [Page.GOAL_SUBSCRIPTIONS]: null,
      [Page.CONVERSATION]: null
    },
    setPageLoading: (page: Page, isLoading: boolean) => {
      set({
        pageLoading: {
          ...get().pageLoading,
          [page]: isLoading
        }
      });
    },

    // AUTH
    authPhase: AuthPhase.INITIALIZING,
    setAuthPhase: (phase: AuthPhase) => {
      if (get().authPhase === phase) return;
      set({ authPhase: phase });
    },

    // LOCAL STORAGE STATE
    introIsDone: false,
    setIntroIsDone: localStoregeHandlers.setIntroIsDone(get, set),
    getIntroIsDone: localStoregeHandlers.getIntroIsDone(get, set),
    initIntroIsDone: localStoregeHandlers.initIntroIsDone(get, set),

    videoPlaybackSpeed: 1,
    setVideoPlaybackSpeed: localStoregeHandlers.setVideoPlaybackSpeed(get, set),
    getVideoPlaybackSpeed: localStoregeHandlers.getVideoPlaybackSpeed(get, set),
    initVideoPlaybackSpeed: localStoregeHandlers.initVideoPlaybackSpeed(get, set),

    imprvApi: null,
    setImprvApi: (imprvApi: ImprvApiService) => {
      set({ imprvApi: imprvApi });
    },
    getImprvApi: () => {
      return get().imprvApi;
    },

    // TOASTS
    toast: null,
    setToast: (toast: ToastModel) => {
      set({ toast: toast });
    },

    // MASTER
    masterHello: null,
    masterSayHello: async () => {
      await callHttpPost(get, set, APIRequestLabel.MASTER_SAY_HELLO, `/master/say_hello`, {
        timezoneOffsetMinutes: -new Date().getTimezoneOffset()
      });

      let apiCallStatus = get().apiCallStatus[APIRequestLabel.MASTER_SAY_HELLO];
      if (apiCallStatus.error) {
        Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
        return;
      }

      set({ masterHello: apiCallStatus.response });
    },

    accountProfile: null,
    getAccountProfile: handlers.getAccountProfile(get, set),

    // GOALS
    createGoal: handlers.createGoal(get, set),
    updateGoal: handlers.updateGoal(get, set),
    publishGoal: handlers.publishGoal(get, set),

    goals: [],
    getGoalsOld: handlers.getGoalsOld(get, set),

    goal: null,
    getGoalOld: handlers.getGoalOld(get, set),
    goalsLibrary: [],
    getGoalsLibrary: handlers.getGoalsLibrary(get, set),
    deleteGoal: handlers.deleteGoal(get, set),
    // CHATBOTS
    chatbotDataEntries: [],
    createChatbotDataEntry: handlers.createChatbotDataEntry(get, set),
    getChatbotDataEntries: handlers.getChatbotDataEntries(get, set),
    updateChatbotDataEntry: handlers.updateChatbotDataEntry(get, set),
    deleteChatbotDataEntry: handlers.deleteChatbotDataEntry(get, set),
    publishChatbot: handlers.publishChatbot(get, set),
    chatbotSettings: null,
    getChatbotSettings: handlers.getChatbotSettings(get, set),
    setChatbotSettings: handlers.setChatbotSettings(get, set),
    updateChatbotSettings: handlers.updateChatbotSettings(get, set),
    setChatbotTestRequest: handlers.setChatbotTestRequest(get, set),

    chatbotTestRequests: new Map(),
    createChatbotTestRequest: handlers.createChatbotTestRequest(get, set),
    getChatbotTestRequest: handlers.getChatbotTestRequest(get, set),
    updateChatbotTestRequest: handlers.updateChatbotTestRequest(get, set),

    // GOAL SUBSCRIPTIONS
    goalSubscriptions: [],
    getGoalSubscriptionsOld: handlers.getGoalSubscriptionsOld(get, set),

    goalSubscription: null,
    getGoalSubscription: handlers.getGoalSubscriptionOld(get, set),

    createGoalSubscription: handlers.createGoalSubscription(get, set),
    updateGoalSubscription: handlers.updateGoalSubscription(get, set),
    goalSubscriptionChangedTo: null,
    setGoalSubscriptionChangedTo: (status: 'ACTIVE' | 'COMPLETED') => {
      set({ goalSubscriptionChangedTo: status });
    },
    syncGoalSubscription: handlers.syncGoalSubscription(get, set),

    // TASK
    task: null,
    //FIXME move this to the AssignmentTaskContext
    taskOutput: {},
    taskErrors: {},
    taskSuccesses: {},
    taskWarnings: {},
    //FIXME the above up to this
    taskDone: false,
    getTask: handlers.getTask(get, set),
    createTask: handlers.createTask(get, set),
    cloneTask: handlers.cloneTask(get, set),
    updateTask: handlers.updateTask(get, set),
    deleteTask: handlers.deleteTask(get, set),

    tasks: [],
    getTasks: handlers.getTasks(get, set),

    createBlock: handlers.createBlock(get, set),
    getBlock: handlers.getBlock(get, set),
    cloneBlock: handlers.cloneBlock(get, set),
    updateBlock: handlers.updateBlock(get, set),
    deleteBlock: handlers.deleteBlock(get, set),

    getBlockExercises: handlers.getBlockExercises(get, set),
    blockExercises: {},

    getBlockAssertionSpecs: handlers.getBlockAssertionSpecs(get, set),
    blockAssertionSpecs: {},

    resetBlockOutput: () => {
      set({ taskOutput: {} });
    },
    setBlockOutput: (blockEntityId: string, value: any) => {
      let stateOverride = {
        taskOutput: {
          ...get().taskOutput,
          [blockEntityId]: value
        }
      };

      set(stateOverride);
    },

    // ASSIGNMENT TASK STATE
    assignmentTaskStates: new Map(),
    getAssignmentTaskState: (nodeEntityId: string, goalSubscriptionId: number) => {
      return (
        get().assignmentTaskStates.get(createAssignmentTaskKey(nodeEntityId, goalSubscriptionId)) ||
        null
      );
    },
    setAssignmentTaskState: (
      nodeEntityId: string,
      goalSubscriptionId: number,
      state: AssignmentTaskStateModel
    ) => {
      const newStates = new Map(get().assignmentTaskStates);
      newStates.set(createAssignmentTaskKey(nodeEntityId, goalSubscriptionId), state);
      set({ assignmentTaskStates: newStates });
    },

    // LIKES
    like: handlers.like(get, set),
    unlike: handlers.unlike(get, set),

    // GARMIN
    garminConnections: [],
    getGarminConnections: handlers.getGarminConnections(get, set),
    createGarminConnection: handlers.createGarminConnection(get, set),
    authorizeGarminConnection: handlers.authorizeGarminConnection(get, set),

    // SCHEDULE
    lastScheduleQueueDate: null,
    setLastScheduleQueueDate: localStoregeHandlers.setLastScheduleQueueDate(get, set),
    getLastScheduleQueueDate: localStoregeHandlers.getLastScheduleQueueDate(get, set),
    initLastScheduleQueueDate: localStoregeHandlers.initLastScheduleQueueDate(get, set),

    availabilities: [],
    getAvailabilities: handlers.getAvailabilities(get, set),
    createOrUpdateAvailability: handlers.createOrUpdateAvailability(get, set),
    schedulingInProgress: false,
    schedulingInProgressTimestamp: null,
    setSchedulingInProgress: (inProgress: boolean, schedulingInProgressTimestamp: number) => {
      const previousTimestamp = get().schedulingInProgressTimestamp;
      if (previousTimestamp && previousTimestamp > schedulingInProgressTimestamp) {
        return;
      }
      set({ schedulingInProgress: inProgress, schedulingInProgressTimestamp });
    },
    // SUPPLIES
    getSupplies: handlers.getSupplies(get, set),
    supplies: {},

    // TAGS
    getTags: handlers.getTags(get, set),
    getTag: handlers.getTag(get, set),
    createTag: handlers.createTag(get, set),
    updateTag: handlers.updateTag(get, set),
    deleteTag: handlers.deleteTag(get, set),
    tags: [],

    // ACTIVATIONS
    getActivationPhraseTemplates: handlers.getActivationPhraseTemplates(get, set),
    createActivationRule: handlers.createActivationRule(get, set),
    updateActivationRule: handlers.updateActivationRule(get, set),
    getActivationRule: handlers.getActivationRule(get, set),
    deleteActivationRule: handlers.deleteActivationRule(get, set),

    getStrategy: handlers.getStrategy(get, set),
    publishStrategy: handlers.publishStrategy(get, set),
    publishTask: handlers.publishTask(get, set),
    createStrategyPath: handlers.createStrategyPath(get, set),
    updateStrategyPath: handlers.updateStrategyPath(get, set),
    deleteStrategyPath: handlers.deleteStrategyPath(get, set),
    createStrategyNodesBatch: handlers.createStrategyNodesBatch(get, set),
    createStrategyNode: handlers.createStrategyNode(get, set),
    updateStrategyNode: handlers.updateStrategyNode(get, set),
    deleteStrategyNode: handlers.deleteStrategyNode(get, set),
    cancelPendingSubscription: handlers.cancelPendingSubscription(get, set),

    // GLOBAL MODALS
    goalSubscriptionStrategyFinishedModalState: {
      isOpen: false,
      goalSubscriptionId: null
    },
    setGoalSubscriptionStrategyFinishedModalState: (
      state: GoalSubscriptionStrategyFinishedModalState
    ) => {
      set({ goalSubscriptionStrategyFinishedModalState: state });
    },

    buyImprvCoinsModalState: {
      isOpen: false
    },
    openBuyImprvCoinsModal: () => {
      set({ buyImprvCoinsModalState: { isOpen: true } });
    },
    closeBuyImprvCoinsModal: () => {
      set({ buyImprvCoinsModalState: { isOpen: false } });
    },

    // EVENTS
    eventEmitter: eventEmitter,
    onEvent: <T>(event: EventType, listener: EventListener<T>) => {
      return get().eventEmitter.on(event, listener);
    },
    emitEvent: <T>(event: EventType, data: T) => {
      get().eventEmitter.emit(event, data);
    },

    // UI -> GAME STATE (for components outside AssignmentTaskContext)
    gameActive: false,
    setGameActive: (active) => set({ gameActive: active }),

    // UI -> MENTOR APPROVE (bridge between FeedbackEditor and navigation)
    mentorApproveHandler: null,
    setMentorApproveHandler: (handler) => set({ mentorApproveHandler: handler }),
    isMentorApproving: false,
    setMentorApproving: (isApproving) => set({ isMentorApproving: isApproving }),

    // UI -> GOAL USERS QUERY (persists filter/search across navigation, scoped per goalEntityId)
    goalUsersQueryState: {},
    setGoalUsersActiveFilter: (goalEntityId, filter) =>
      set((state) => ({
        goalUsersQueryState: {
          ...state.goalUsersQueryState,
          [goalEntityId]: {
            searchQuery: state.goalUsersQueryState[goalEntityId]?.searchQuery ?? '',
            activeFilter: filter
          }
        }
      })),
    setGoalUsersSearchQuery: (goalEntityId, query) =>
      set((state) => ({
        goalUsersQueryState: {
          ...state.goalUsersQueryState,
          [goalEntityId]: {
            activeFilter:
              state.goalUsersQueryState[goalEntityId]?.activeFilter ?? GoalUsersFilter.ALL,
            searchQuery: query
          }
        }
      }))
  }),
  shallow
);
